<template>
    <div class="presence-bar" v-if="isConnected && hasUsers">
        <AvatarGroup>
            <!-- Editing users with overlay badge -->
            <template v-for="user in editingUsers" :key="user.user_id">
                <OverlayBadge
                    value="✎"
                    severity="info"
                    size="small"
                    class="inline-flex"
                >
                    <Avatar
                        :image="user.photo_url ?? undefined"
                        :label="
                            user.photo_url
                                ? undefined
                                : getFirstLetter(user.user_name)
                        "
                        shape="circle"
                        size="large"
                        :pt="{ root: { class: 'editing-avatar' } }"
                        v-tooltip.top="getTooltipText(user)"
                        class="custom-avatar"
                    />
                </OverlayBadge>
            </template>
            <!-- Viewing users without badge -->
            <template v-for="user in viewingUsers" :key="user.user_id">
                <Avatar
                    :label="
                        user.photo_url
                            ? undefined
                            : getFirstLetter(user.user_name)
                    "
                    :image="user.photo_url ?? undefined"
                    shape="circle"
                    size="large"
                    v-tooltip.top="getTooltipText(user)"
                    class="custom-avatar"
                />
            </template>
        </AvatarGroup>

        <div v-if="!isConnected" class="presence-bar-status">
            <span class="presence-bar-status-text">Reconnecting...</span>
        </div>
    </div>
</template>

<script setup lang="ts">
import Avatar from "primevue/avatar";
import AvatarGroup from "primevue/avatargroup";
import OverlayBadge from "primevue/overlaybadge";
import { computed } from "vue";
import { usePresenceManager, UserActivity } from "../stores/activity-store";

const { isConnected, editingUsers, viewingUsers } = usePresenceManager();

const hasUsers = computed(
    () => editingUsers.value.length > 0 || viewingUsers.value.length > 0
);

const getTooltipText = (user: UserActivity) => {
    let text = `${user.user_name} is `;
    if (user.is_editing) {
        text += "editing";
    } else {
        text += "viewing";
    }

    return text;
};

const getFirstLetter = (displayName: string) => {
    return displayName?.charAt(0) || "?";
};
</script>

<style scoped>
.presence-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 24px;
    padding: 8px 16px;
}

.presence-bar-status {
    margin-left: auto;
}

.presence-bar-status-text {
    font-size: 14px;
    color: #5f6368;
}

.custom-avatar {
    background-color: var(--stekz-biz-lightest-color) !important;
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
}

:deep(.editing-avatar) {
    background-color: var(--stekz-biz-lightest-color) !important;
}

:deep(.p-avatar-text) {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
}
</style>
